<script setup lang="ts">
import { ref, watchEffect } from 'vue'

// Define Props
const props = defineProps<{
  modelValue: number | string | null | undefined | boolean
  min?: number | string
  max?: number | string
  step?: number
  label?: string
  error?: string | null
  readonly?: boolean
  placeholder?: string
  width?: string
}>()

// Define Emits
const emit = defineEmits<{
  (e: 'update:modelValue', value: number | string | null): void
  (e: 'blur'): void
}>()

// Reactive local value
const localValue = ref<string | number>('') // Default to empty string

// Sync `localValue` with `modelValue` when the component loads
watchEffect(() => {
  localValue.value = (typeof props.modelValue === 'string' || typeof props.modelValue === 'number') ? props.modelValue : '' // Convert `null` to empty string
})

// Handle input changes
const handleInput = (e: Event) => {
  if (props.readonly) return // Prevent input changes if readonly
  
  const input = e.target as HTMLInputElement
  const value = input.value

  if (value === '') {
    localValue.value = ''
    emit('update:modelValue', null) // Emit `null` if empty
    return
  }

  const numValue = Number(value)
  if (!isNaN(numValue)) {
    if (props.min !== undefined && numValue < parseInt(props.min.toString())) {
      localValue.value = props.min
      emit('update:modelValue', props.min)
    } else if (props.max !== undefined && numValue > parseInt(props.max.toString())) {
      localValue.value = props.max
      emit('update:modelValue', props.max)
    } else {
      localValue.value = numValue
      emit('update:modelValue', numValue)
    }
  }
}

// Handle blur event
const handleBlur = () => {
  if (localValue.value === '') {
    localValue.value = props.min !== undefined ? props.min : 0
    emit('update:modelValue', localValue.value)
  }
  emit('blur')
}
</script>

<template>
  <div>
    <!-- Label -->
    <label v-if="label" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
      {{ label }}
    </label>

    <!-- Input Field -->
    <div class="">
      <input
        type="number"
        :value="localValue"
        @input="handleInput"
        @blur="handleBlur"
        :min="min"
        :max="max"
        :step="step"
        :readonly="readonly"
        :placeholder="placeholder"
        class="flex-1 px-3 py-2 border text-gray-900 dark:text-white text-center border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 "
        :class="[
          error
            ? 'border-red-300 dark:border-red-700'
            : 'border-gray-200 dark:border-gray-700',
          readonly
            ? 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed'
            : 'bg-white dark:bg-gray-900',
          width ? 'w-[' + width + ']' : 'w-full'
        ]"
      />
    </div>

    <!-- Error Message -->
    <p v-if="error" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ error }}</p>
  </div>
</template>
